import moment from "moment";

export type SpreadsheetDateResult = { date: string } | { error: string };

const SEPARATORS = ["-", "/", "."];
const MONTH_NAMES = ["MMMM", "MMM", "MMM."];

// Numeric dates are read month first (US), so "3/4/1976" is March 4.
const NUMERIC_DATE_FORMATS = SEPARATORS.flatMap((separator) => [
  `YYYY${separator}MM${separator}DD`,
  `MM${separator}DD${separator}YYYY`,
]);
const TEXT_DATE_FORMATS = MONTH_NAMES.flatMap((month) => [
  `${month} DD, YYYY`,
  `${month} DD YYYY`,
  `${month} Do, YYYY`,
  `${month} Do YYYY`,
  `DD ${month} YYYY`,
  `Do ${month} YYYY`,
  `DD-${month}-YYYY`,
]);
const FULL_DATE_FORMATS = [...NUMERIC_DATE_FORMATS, ...TEXT_DATE_FORMATS];
const TWO_DIGIT_YEAR_FORMATS = FULL_DATE_FORMATS.filter((format) => format.endsWith("YYYY")).map((format) =>
  format.replace("YYYY", "YY")
);
const MISSING_DAY_FORMATS = [
  "YYYY",
  ...SEPARATORS.flatMap((separator) => [`YYYY${separator}MM`, `MM${separator}YYYY`]),
  ...MONTH_NAMES.map((month) => `${month} YYYY`),
];

const LEADING_WEEKDAY = /^(?:mon|tue|wed|thu|fri|sat|sun)[a-z]*\.?,? /i;
const TRAILING_TIME = /(?:,? |t)\d{1,2}:\d{2}.*$/i;

const matchesFormat = (value: string, formats: string[]): moment.Moment | undefined => {
  const parsed = moment.utc(value, formats, true);
  return parsed.isValid() ? parsed : undefined;
};

// A two-digit year is the latest one that doesn't put the date in the future: "78" is 1978, "05" is 2005.
const withFullYear = (date: moment.Moment): moment.Moment => {
  const today = moment.utc();
  const century = today.year() - (today.year() % 100);
  const fullDate = moment.utc({ year: century + (date.year() % 100), month: date.month(), date: date.date() });
  return fullDate.isAfter(today, "day") ? fullDate.subtract(100, "years") : fullDate;
};

// A one-digit year ("24-May-7") is a typo, not a year to guess. ISO dates end with the day, not the year.
const hasOneDigitYear = (value: string): boolean => /(^|\D)\d$/.test(value) && !/^\d{4}\D/.test(value);

/**
 * Reads a date typed into a spreadsheet cell and returns it as "YYYY-MM-DD".
 * A weekday before the date and a time after it are ignored. Dates without a day are rejected.
 */
export function parseSpreadsheetDate(rawValue: string): SpreadsheetDateResult {
  const unreadable = { error: `Date "${rawValue}" isn't a readable date.` };
  const trimmedValue = rawValue.trim().replace(/\s+/g, " ").replace(LEADING_WEEKDAY, "").replace(TRAILING_TIME, "");
  if (hasOneDigitYear(trimmedValue)) return unreadable;
  const value = trimmedValue
    // Single digits are zero-padded so the formats only need two-digit month and day tokens.
    .replace(/\b(\d)\b/g, "0$1")
    // moment only knows "Sep", not "Sept".
    .replace(/\bsept\b/i, "Sep");

  const date = matchesFormat(value, FULL_DATE_FORMATS);
  if (date) return { date: date.format("YYYY-MM-DD") };
  if (matchesFormat(value, MISSING_DAY_FORMATS)) {
    return { error: `Date "${rawValue}" needs a day, month and year.` };
  }
  const twoDigitYearDate = matchesFormat(value, TWO_DIGIT_YEAR_FORMATS);
  if (twoDigitYearDate) {
    const fullDate = withFullYear(twoDigitYearDate);
    return fullDate.isValid() ? { date: fullDate.format("YYYY-MM-DD") } : unreadable;
  }
  return unreadable;
}
